import React, { useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Alert from "react-bootstrap/Alert";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationCircle } from "@fortawesome/free-solid-svg-icons";
import "./AssessmentStatus.css";

function ResultsStatus({
  classDetails,
  classAssessmentsValues = [],
  teachers = [],
  schoolid,
  teacherid,
  selectAssessmentId,
}) {
  console.log("=== RESULTS STATUS DEBUG START ===");
  console.log("Raw props received:");
  console.log("  - classDetails:", classDetails);
  console.log("  - classAssessmentsValues:", classAssessmentsValues);
  console.log("  - classAssessmentsValues length:", classAssessmentsValues.length);
  console.log("  - classAssessmentsValues type:", typeof classAssessmentsValues);
  console.log("  - Is array?", Array.isArray(classAssessmentsValues));
  
  // Log each assessment in detail
  if (classAssessmentsValues && classAssessmentsValues.length > 0) {
    console.log("Individual assessments:");
    classAssessmentsValues.forEach((assessment, index) => {
      console.log(`  Assessment ${index}:`, {
        id: assessment.id,
        name: assessment.name,
        assessmentid: assessment.assessmentid,
        grade_level1: assessment.grade_level1,
        grade_level2: assessment.grade_level2,
        grade_level3: assessment.grade_level3,
        grade_level4: assessment.grade_level4,
        task: assessment.task,
        instances: assessment.instances,
        fullObject: assessment
      });
    });
  } else {
    console.log("No assessments in classAssessmentsValues array");
  }

  console.log("  - teachers:", teachers);
  console.log("  - schoolid:", schoolid);
  console.log("  - teacherid:", teacherid);
  console.log("  - selectAssessmentId:", selectAssessmentId);

  const navigate = useNavigate();

  const assessmentOrder = useMemo(
    () => ({
      baseline: 1,
      midpoint: 2,
      "end of year": 3,
    }),
    []
  );

  const getIconFileName = useCallback((assessmentName) => {
    console.log("  getIconFileName called with:", assessmentName);
    let name = assessmentName?.toLowerCase() || "";

    if (name.includes("baseline") || name.includes("autumn")) {
      console.log("  -> returning 'autumn'");
      return "autumn";
    } else if (name.includes("midpoint") || name.includes("spring")) {
      console.log("  -> returning 'spring'");
      return "spring";
    } else if (name.includes("end of year") || name.includes("summer")) {
      console.log("  -> returning 'summer'");
      return "summer";
    }

    // Remove "Instance" and handle task-based naming
    name = name.replace(" instance", "").trim();
    const words = name.split(" ");
    const result = words.slice(0, 2).join("") || words[0];
    console.log(`  -> processed '${assessmentName}' to '${result}'`);
    return result;
  }, []);

  const classAssessmentsArray = useMemo(() => {
    console.log("=== PROCESSING ASSESSMENTS IN USEMEMO ===");
    console.log("Input classAssessmentsValues:", classAssessmentsValues);
    
    const sortAssessments = (a, b) => {
      const getSortKey = (name) => {
        name = name.toLowerCase();
        if (name.includes("baseline") || name.includes("autumn"))
          return assessmentOrder["baseline"];
        if (name.includes("midpoint") || name.includes("spring"))
          return assessmentOrder["midpoint"];
        if (name.includes("end of year") || name.includes("summer"))
          return assessmentOrder["end of year"];
        return 4; // Default sort key
      };

      const aKey = getSortKey(a.name);
      const bKey = getSortKey(b.name);
      console.log(`  Sorting ${a.name} (${aKey}) vs ${b.name} (${bKey})`);
      return aKey - bKey;
    };

    const mappedAssessments = classAssessmentsValues.map((assessment, index) => {
      console.log(`  Processing assessment ${index}:`, assessment);
      const imageName = getIconFileName(assessment.name);
      const mapped = {
        ...assessment,
        imageName: imageName,
      };
      console.log(`  Mapped to:`, mapped);
      return mapped;
    });

    console.log("Before sorting:", mappedAssessments);
    const sortedAssessments = mappedAssessments.sort(sortAssessments);
    console.log("After sorting:", sortedAssessments);

    return sortedAssessments;
  }, [classAssessmentsValues, getIconFileName, assessmentOrder]);

  console.log("=== FINAL PROCESSED DATA ===");
  console.log("classAssessmentsArray:", classAssessmentsArray);
  console.log("classAssessmentsArray length:", classAssessmentsArray.length);

  const toAssessmentResults = useCallback(
    (assessment) => {
      console.log("=== NAVIGATING TO RESULTS ===");
      console.log("Assessment clicked:", assessment);
      console.log("Navigation state:", {
        assessment,
        assessmentId: assessment.assessmentid,
        classDetails,
        classAssessments: classAssessmentsValues,
        classAssessmentsArray,
        teachers,
        gradeLevel: classDetails.grade_level,
        schoolid,
        teacherid,
      });

      navigate("/studentsresults", {
        state: {
          assessment,
          assessmentId: assessment.assessmentid,
          classDetails,
          classAssessments: classAssessmentsValues,
          classAssessmentsArray,
          teachers,
          gradeLevel: classDetails.grade_level,
          schoolid,
          teacherid,
        },
      });
    },
    [
      navigate,
      classDetails,
      classAssessmentsValues,
      classAssessmentsArray,
      teachers,
      schoolid,
      teacherid,
      selectAssessmentId,
    ]
  );

  console.log("=== RENDER DECISION ===");
  console.log("Will show 'No Results Available'?", classAssessmentsArray.length === 0);
  console.log("Will show assessment cards?", classAssessmentsArray.length > 0);

  if (classAssessmentsArray.length > 0) {
    console.log("Assessment cards to render:");
    classAssessmentsArray.forEach((assessment, index) => {
      console.log(`  Card ${index}:`, {
        id: assessment.id,
        name: assessment.name,
        imageName: assessment.imageName,
        displayName: assessment.name.replace(" Instance", ""),
        isSelected: selectAssessmentId === assessment.id
      });
    });
  }

  return (
    <Row>
      {classAssessmentsArray.length === 0 ? (
        <Col>
          <Alert variant="info" className="text-center">
            <FontAwesomeIcon
              icon={faExclamationCircle}
              size="3x"
              className="mb-3"
            />
            <h4>No Results Available</h4>
            <p>
              Once your students begin screening, the results will start to
              populate here.
            </p>
          </Alert>
        </Col>
      ) : (
        classAssessmentsArray.map((classAssessment, index) => {
          console.log(`=== RENDERING CARD ${index} ===`);
          console.log("Assessment:", classAssessment);
          console.log("Image source:", `/assets/imgs/${classAssessment.imageName}-icon.png`);
          console.log("Display name:", classAssessment.name.replace(" Instance", ""));
          
          return (
            <Col className="col-4" key={classAssessment.id || index}>
              <div
                className={`assessment-card ${
                  selectAssessmentId === classAssessment.id ? "selected" : ""
                }`}
                onClick={() => toAssessmentResults(classAssessment)}
              >
                <div className="card-image">
                  <img
                    src={`/assets/imgs/${classAssessment.imageName}-icon.png`}
                    className="season-assessment-icon"
                    alt={classAssessment.name}
                  />
                </div>
                <div className="card-content">
                  <span>{classAssessment.name.replace(" Instance", "")}</span>
                </div>
              </div>
            </Col>
          );
        })
      )}
    </Row>
  );
}

ResultsStatus.propTypes = {
  classDetails: PropTypes.shape({
    grade_level: PropTypes.string,
    grade_level1: PropTypes.string,
    grade_level2: PropTypes.string,
    grade_level3: PropTypes.string,
    grade_level4: PropTypes.string,
    trial_class: PropTypes.bool,
  }).isRequired,
  classAssessmentsValues: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    })
  ).isRequired,
  teachers: PropTypes.array,
  schoolid: PropTypes.string,
  teacherid: PropTypes.string,
  selectAssessmentId: PropTypes.string,
};

export default ResultsStatus;